import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConfiguredDemoDataMode } from './demoApi.ts';
import { fetchAuthSession, fetchLoginUsers, loginWithAuthIdentity, logoutAuthSession, type AuthLoginResponse, type AuthUser } from './authApi.ts';
import { clearStoredSessionToken, getStoredSessionToken, storeSessionToken } from './authStorage.ts';

const AUTH_QUERY_KEY_ROOT = ['field-service-technique-auth'];

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'bypassed';

export function useAuthSession() {
  const queryClient = useQueryClient();
  const configuredMode = getConfiguredDemoDataMode();
  const [authBypassed, setAuthBypassed] = useState(configuredMode === 'local');
  const [sessionRevision, setSessionRevision] = useState(0);

  useEffect(() => {
    if (configuredMode === 'local') {
      setAuthBypassed(true);
      return;
    }
    setAuthBypassed(false);
  }, [configuredMode]);

  const usersQuery = useQuery({
    queryKey: [...AUTH_QUERY_KEY_ROOT, 'users'],
    queryFn: ({ signal }) => fetchLoginUsers(signal),
    enabled: configuredMode !== 'local' && !authBypassed,
    retry: false,
    staleTime: 60_000,
  });

  const sessionQuery = useQuery({
    queryKey: [...AUTH_QUERY_KEY_ROOT, 'session', sessionRevision],
    queryFn: ({ signal }) => fetchAuthSession(signal),
    enabled: configuredMode !== 'local' && !authBypassed,
    retry: false,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!sessionQuery.data?.authenticated && getStoredSessionToken()) {
      clearStoredSessionToken();
    }
  }, [sessionQuery.data?.authenticated]);

  const loginMutation = useMutation({
    mutationFn: (payload: { email?: string; userId?: string; password: string }) => loginWithAuthIdentity(payload),
    onSuccess: async (result: AuthLoginResponse) => {
      if (result.sessionToken) {
        storeSessionToken(result.sessionToken);
      }
      setAuthBypassed(false);
      setSessionRevision((current) => current + 1);
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY_ROOT });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => logoutAuthSession(),
    onSuccess: async () => {
      clearStoredSessionToken();
      setSessionRevision((current) => current + 1);
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY_ROOT });
    },
  });

  const user = sessionQuery.data?.authenticated ? sessionQuery.data.user : null;
  const status: AuthStatus = authBypassed
    ? 'bypassed'
    : usersQuery.isLoading || sessionQuery.isLoading || loginMutation.isPending || logoutMutation.isPending
      ? 'loading'
      : user
        ? 'authenticated'
        : 'unauthenticated';

  const error = useMemo(() => {
    if (loginMutation.error) return loginMutation.error;
    if (logoutMutation.error) return logoutMutation.error;
    if (sessionQuery.error) return sessionQuery.error;
    if (usersQuery.error) return usersQuery.error;
    return null;
  }, [loginMutation.error, logoutMutation.error, sessionQuery.error, usersQuery.error]);

  return {
    status,
    user,
    authBypassed,
    loginUsers: (usersQuery.data as AuthUser[] | undefined) ?? [],
    error,
    isSubmitting: loginMutation.isPending || logoutMutation.isPending,
    async login(payload: { email?: string; userId?: string; password: string }) {
      return loginMutation.mutateAsync(payload);
    },
    async logout() {
      return logoutMutation.mutateAsync();
    },
  };
}
